import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Product, Category, Brand, apiService } from '../services/api';
import { useCart } from '../context/CartContext';
import QuickAddToCartModal from '../components/QuickAddToCartModal';
import LogoLoader from '../components/ui/LogoLoader';

const ProductSignature: React.FC = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState('default');
  const [onlyPromotions, setOnlyPromotions] = useState(false);
  const [onlyNewArrivals, setOnlyNewArrivals] = useState(false);
  const [onlyTrending, setOnlyTrending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  // Quick add modal state
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddProduct, setQuickAddProduct] = useState<Product | null>(null);
  // Collapsible sections state
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);
  const [brandsExpanded, setBrandsExpanded] = useState(false);

  // Helper function to calculate available stock from variants (total - reserved)
  const calculateTotalStock = (product: Product): number => {
    if (!product.variants || product.variants.length === 0) {
      return product.in_stock || 0;
    }
    
    return product.variants.reduce((total, variant) => {
      if (!variant.sizes || variant.sizes.length === 0) {
        return total;
      }
      
      const variantStock = variant.sizes.reduce((sizeTotal, size) => {
        // Calculate available stock (total stock minus reserved stock)
        const totalStock = size.stock_quantity || 0;
        const reservedStock = size.reserved_quantity || 0;
        const availableStock = Math.max(0, totalStock - reservedStock);
        return sizeTotal + availableStock;
      }, 0);
      
      return total + variantStock;
    }, 0);
  };

  const location = useLocation();

  // Fetch initial data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [productsResponse, categoriesData] = await Promise.all([
        apiService.getProducts(),
        apiService.get('/categories/public'),
      ]);

      setProducts(productsResponse.data || []);
      const categoriesArray = Array.isArray(categoriesData)
        ? categoriesData
        : categoriesData?.data || [];
      setCategories(categoriesArray);

      // Build brands from products
      const uniqueBrands = Array.from(
        new Map(
          (productsResponse.data || [])
            .filter((product) => product.brand && product.brand_id)
            .map((product) => [product.brand_id, product.brand])
        ).values()
      ).filter((brand): brand is Brand => brand !== undefined);
      setBrands(uniqueBrands);
    } catch (error) {
      setProducts([]);
      setCategories([]);
      setBrands([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Removed older fetchData function (replaced by useCallback version above)

  // Helpers (must be declared before usage)
  // Compute the 6 most recent active products as "New Arrivals"
  const newArrivalIds = useMemo(() => {
    const sorted = [...products]
      .filter(p => p.is_active)
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
      .slice(0, 6)
      .map(p => p.id);
    return new Set<number>(sorted);
  }, [products]);
  const isPromotional = (product: Product): boolean => {
    const promoPrice = product.promotional_price ?? product.discounted_price;
    return Boolean(
      product.has_promotion ||
      (promoPrice !== undefined && promoPrice < product.price) ||
      (product.discount_percentage && product.discount_percentage > 0)
    );
  };

  const isNewArrival = (product: Product): boolean => newArrivalIds.has(product.id);

  // Also react to URL changes (e.g., navigating between subcategory links while staying on the page)
  useEffect(() => {
    if (!categories || categories.length === 0) return;
    const params = new URLSearchParams(location.search);
    const categorySlug = params.get('category');
    const brandSlug = params.get('brand');
    const promotionsParam = params.get('promotions');
    const newParam = params.get('new');

    // Category by slug
    if (categorySlug) {
      const matched = categories.find((c: any) => c.slug === categorySlug);
      if (matched) {
        setSelectedCategory(matched.id);
      } else {
        setSelectedCategory(null);
      }
    } else {
      setSelectedCategory(null);
    }

    // Brand by slug
    if (brandSlug) {
      const matched = brands.find((b: any) => b.slug === brandSlug);
      if (matched) {
        setSelectedBrand(matched.id);
      } else {
        setSelectedBrand(null);
      }
    } else {
      setSelectedBrand(null);
    }

    // Promotions filter toggle via query param
    if (promotionsParam === '1' || promotionsParam === 'true') {
      setOnlyPromotions(true);
    } else if (promotionsParam === '0' || promotionsParam === 'false' || promotionsParam === null) {
      setOnlyPromotions(false);
    }

    // New arrivals filter toggle via query param
    if (newParam === '1' || newParam === 'true') {
      setOnlyNewArrivals(true);
    } else if (newParam === '0' || newParam === 'false' || newParam === null) {
      setOnlyNewArrivals(false);
    }

    // Trending filter toggle via query param
    const trendingParam = params.get('trending');
    if (trendingParam === '1' || trendingParam === 'true') {
      setOnlyTrending(true);
    } else if (trendingParam === '0' || trendingParam === 'false' || trendingParam === null) {
      setOnlyTrending(false);
    }

    // Search query
    const searchParam = params.get('search');
    setSearchQuery(searchParam || '');
  }, [location.search, categories, brands]);

  // Resolve selected category to include subcategories if a main category is chosen
  const selectedCategoryIds = useMemo(() => {
    if (!selectedCategory) return null;
    // Gather subcategory IDs under the selected category by parent_id
    const subIds = categories
      .filter((c: any) => c.parent_id === selectedCategory)
      .map((c: any) => c.id);
    return [selectedCategory, ...subIds];
  }, [selectedCategory, categories]);

  const filteredProducts = products.filter(product => {
    if (selectedCategoryIds && !selectedCategoryIds.includes(product.category_id)) return false;
    if (selectedBrand && product.brand_id !== selectedBrand) return false;
    if (onlyPromotions && !isPromotional(product)) return false;
    if (onlyNewArrivals && !isNewArrival(product)) return false;
    if (onlyTrending && !product.is_trending) return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = product.name.toLowerCase().includes(query);
      const matchesDescription = product.description?.toLowerCase().includes(query);
      const matchesCategory = typeof product.category === 'object' && product.category?.name?.toLowerCase().includes(query);
      const matchesBrand = typeof product.brand === 'object' && product.brand?.name?.toLowerCase().includes(query);
      if (!matchesName && !matchesDescription && !matchesCategory && !matchesBrand) return false;
    }
    
    return product.is_active;
  });

  const selectedCategoryObj = selectedCategory
    ? categories.find((c) => c.id === selectedCategory) || null
    : null;
  const hasAnyFilter = selectedCategory !== null || selectedBrand !== null;
  const recommended = products.filter(p => p.is_active).slice(0, 6);
  const promoCount = products.filter(p => p.is_active && isPromotional(p)).length;
  const newArrivalCount = products.filter(p => p.is_active && newArrivalIds.has(p.id)).length;
  const trendingCount = products.filter(p => p.is_active && p.is_trending).length;


  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        // Sort by creation date (newest first) as default
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
  });

  // Reset to first page whenever filters or sorting change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedBrand, onlyPromotions, onlyNewArrivals, onlyTrending, sortBy]);

  // Pagination calculations
  const totalItems = sortedProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedProducts = sortedProducts.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const nums: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) nums.push(i);
    return nums;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price) + ' Ks';
  };

  const getCategoryProductCount = (categoryId: number) => {
    return products.filter(p => p.category_id === categoryId && p.is_active).length;
  };

  const getBrandProductCount = (brandId: number) => {
    return products.filter(p => p.brand_id === brandId && p.is_active).length;
  };

  // Main category total = main's products + all subcategories' products
  const getMainCategoryTotalCount = (mainCategoryId: number) => {
    // Count products directly tagged to the main category
    const mainCount = getCategoryProductCount(mainCategoryId);
    // Find all subcategories under this main
    const subIds = categories
      .filter((c: any) => c.type === 'subcategory' && c.parent_id === mainCategoryId)
      .map((c: any) => c.id);
    // Sum products in subcategories
    const subsCount = subIds.reduce((sum, subId) => sum + getCategoryProductCount(subId), 0);
    return mainCount + subsCount;
  };

  // Build hierarchical categories (main with children)
  const hierarchicalCategories = (() => {
    const mains = categories.filter((c: any) => c.type === 'category');
    const subs = categories.filter((c: any) => c.type === 'subcategory');
    return mains.map((m: any) => ({
      ...m,
      children: subs.filter((s: any) => s.parent_id === m.id)
    }));
  })();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LogoLoader size="lg" text="Loading products..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumbs Section */}
      <div className="border-b border-gray-200 pt-16 pb-4" style={{ backgroundColor: '#f7f1f0' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center justify-center space-x-2 text-sm text-gray-600">
            <span className="hover:text-gray-800 transition-colors">
              Home
            </span>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">Products</span>
          </nav>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Mobile Filter Toggle Button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
              className="w-full bg-white rounded-lg shadow-sm p-4 flex items-center justify-between text-gray-900 font-medium"
            >
              <span className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filters & Categories
              </span>
              <svg 
                className={`w-5 h-5 transition-transform ${isMobileFilterOpen ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Sidebar */}
          <div className={`lg:w-1/4 ${isMobileFilterOpen ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-lg shadow-sm p-6 lg:sticky lg:top-8 max-h-[calc(100vh-100px)] overflow-y-auto">
              {/* Filters (moved to top) */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">FILTERS</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      setOnlyPromotions(prev => {
                        const next = !prev;
                        if (next) {
                          setOnlyNewArrivals(false);
                          setOnlyTrending(false);
                          setSelectedCategory(null);
                          setSelectedBrand(null);
                        }
                        return next;
                      });
                      setIsMobileFilterOpen(false);
                    }}
                    className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-md text-sm ${
                      onlyPromotions
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <span>On Promotion</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{promoCount}</span>
                  </button>
                  <button
                    onClick={() => {
                      setOnlyNewArrivals(prev => {
                        const next = !prev;
                        if (next) {
                          setOnlyPromotions(false);
                          setOnlyTrending(false);
                          setSelectedCategory(null);
                          setSelectedBrand(null);
                        }
                        return next;
                      });
                      setIsMobileFilterOpen(false);
                    }}
                    className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-md text-sm ${
                      onlyNewArrivals
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <span>New Arrivals</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{newArrivalCount}</span>
                  </button>
                  <button
                    onClick={() => {
                      setOnlyTrending(prev => {
                        const next = !prev;
                        if (next) {
                          setOnlyPromotions(false);
                          setOnlyNewArrivals(false);
                          setSelectedCategory(null);
                          setSelectedBrand(null);
                        }
                        return next;
                      });
                      setIsMobileFilterOpen(false);
                    }}
                    className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-md text-sm ${
                      onlyTrending
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <span>Trending</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{trendingCount}</span>
                  </button>
                </div>
              </div>
              {/* Categories */}
              <div className="mb-6 border-b border-gray-200 pb-4">
                <button
                  onClick={() => setCategoriesExpanded(!categoriesExpanded)}
                  className="flex items-center justify-between w-full text-lg font-semibold text-gray-900 mb-3 hover:text-gray-700"
                >
                  <span>CATEGORIES</span>
                  <svg 
                    className={`w-5 h-5 transition-transform ${categoriesExpanded ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {categoriesExpanded && (
                  <div className="space-y-1 max-h-96 overflow-y-auto">
                    <button
                      onClick={() => {
                        setSelectedCategory(null);
                        setSelectedBrand(null);
                        setOnlyPromotions(false);
                        setOnlyNewArrivals(false);
                        setOnlyTrending(false);
                        setIsMobileFilterOpen(false);
                      }}
                      className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-md text-sm ${
                        selectedCategory === null && !onlyPromotions && !onlyNewArrivals && !onlyTrending && selectedBrand === null
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <span>All Products</span>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                        {products.filter(p => p.is_active).length}
                      </span>
                    </button>

                    {/* Hierarchical main and sub categories */}
                    {hierarchicalCategories.map((main: any) => (
                      <div key={main.id} className="space-y-1">
                        {/* Main category */}
                        <button
                          onClick={() => {
                            setSelectedCategory(main.id);
                            setSelectedBrand(null);
                            setOnlyPromotions(false);
                            setOnlyNewArrivals(false);
                            setOnlyTrending(false);
                            setIsMobileFilterOpen(false);
                          }}
                          className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-md text-sm ${
                            selectedCategory === main.id
                              ? 'bg-blue-50 text-blue-700 font-medium'
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          <span className="font-medium">{main.name}</span>
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                            {getMainCategoryTotalCount(main.id)}
                          </span>
                        </button>

                        {/* Subcategories indented */}
                        {main.children && main.children.length > 0 && (
                          <div className="pl-4">
                            {main.children.map((sub: any) => (
                              <button
                                key={sub.id}
                                onClick={() => {
                                  setSelectedCategory(sub.id);
                                  setSelectedBrand(null);
                                  setOnlyPromotions(false);
                                  setOnlyNewArrivals(false);
                                  setOnlyTrending(false);
                                  setIsMobileFilterOpen(false);
                                }}
                                className={`flex items-center justify-between w-full text-left px-3 py-1.5 rounded-md text-xs ${
                                  selectedCategory === sub.id
                                    ? 'bg-blue-50 text-blue-700 font-medium'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                              >
                                <span>
                                  {sub.name}
                                </span>
                                <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                  {getCategoryProductCount(sub.id)}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Brands */}
              <div className="mb-6 border-b border-gray-200 pb-4">
                <button
                  onClick={() => setBrandsExpanded(!brandsExpanded)}
                  className="flex items-center justify-between w-full text-lg font-semibold text-gray-900 mb-3 hover:text-gray-700"
                >
                  <span>BRANDS</span>
                  <svg 
                    className={`w-5 h-5 transition-transform ${brandsExpanded ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {brandsExpanded && (
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    <button
                      onClick={() => {
                        setSelectedBrand(null);
                        setIsMobileFilterOpen(false);
                      }}
                      className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-md text-sm ${
                        selectedBrand === null 
                          ? 'bg-blue-50 text-blue-700 font-medium' 
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <span>All Brands</span>
                    </button>
                    
                    {brands.map((brand) => (
                      <button
                        key={brand.id}
                        onClick={() => {
                          setSelectedBrand(brand.id);
                          // single-select behavior across sidebar
                          setSelectedCategory(null);
                          setOnlyPromotions(false);
                          setOnlyNewArrivals(false);
                          setOnlyTrending(false);
                          setIsMobileFilterOpen(false);
                        }}
                        className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-md text-sm ${
                          selectedBrand === brand.id 
                            ? 'bg-blue-50 text-blue-700 font-medium' 
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <span>{brand.name}</span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                          {getBrandProductCount(brand.id)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Clear Filters */}
              {(selectedCategory || selectedBrand || onlyPromotions || onlyNewArrivals || onlyTrending) && (
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setSelectedBrand(null);
                    setOnlyPromotions(false);
                    setOnlyNewArrivals(false);
                    setOnlyTrending(false);
                  }}
                  className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-gray-600 text-sm">
                  Showing {totalItems === 0 ? 0 : startIndex + 1}-{endIndex} of {totalItems} results
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="default">Default Sorting</option>
                  <option value="name">Sort by Name</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
                
                <div className="flex border border-gray-300 rounded-md">
                  <button className="p-2 border-r border-gray-300 hover:bg-gray-50">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button className="p-2 hover:bg-gray-50">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            {sortedProducts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 103.5 10.5a7.5 7.5 0 0013.15 6.15z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">No products found</h3>
                <p className="text-gray-600 mb-6">
                  {selectedCategoryObj
                    ? `We couldn’t find any products in “${selectedCategoryObj.name}”.`
                    : 'We couldn’t find any products matching your filters.'}
                </p>
                <div className="flex items-center justify-center gap-3">
                  {hasAnyFilter && (
                    <button
                      onClick={() => { setSelectedCategory(null); setSelectedBrand(null); }}
                      className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
                    >
                      Clear filters
                    </button>
                  )}
                  <button
                    onClick={() => { setSelectedCategory(null); setSelectedBrand(null); navigate('/product-signature'); }}
                    className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
                  >
                    View all products
                  </button>
                </div>

                {recommended.length > 0 && (
                  <div className="mt-10 px-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">You might like</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {recommended.map((product) => (
                        <div key={product.id} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
                          <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden bg-gray-100">
                            <img
                              src={product.product_images && product.product_images.length > 0 
                                ? product.product_images[0] 
                                : 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&h=400&fit=crop'}
                              alt={product.name}
                              className="h-48 w-full object-cover object-center"
                            />
                          </div>
                          <div className="p-4">
                            <div className="text-sm text-gray-900 truncate mb-1">{product.name}</div>
                            <div className="text-sm font-semibold text-gray-900">{formatPrice(product.price)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {paginatedProducts.map((product) => (
                  <div key={product.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow group">
                      <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden bg-gray-200 relative">
                        <Link to={`/products/${product.slug}`} className="block">
                          <img
                            src={product.product_images && product.product_images.length > 0 
                              ? product.product_images[0] 
                              : 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&h=400&fit=crop'
                            }
                            alt={product.name}
                            className="h-48 sm:h-64 w-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                          />
                          {product.hover_image && (
                            <img
                              src={product.hover_image}
                              alt={`${product.name} hover`}
                              className="h-48 sm:h-64 w-full object-cover object-center absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                            />
                          )}
                        </Link>
                        {product.is_featured && (
                          <div className="absolute top-1 sm:top-2 left-1 sm:left-2">
                            <span className="bg-yellow-500 text-white text-xs font-medium px-1.5 py-0.5 sm:px-2 sm:py-1 rounded">
                              Featured
                            </span>
                          </div>
                        )}
                        {(product.has_promotion || (product.promotional_price && product.promotional_price < product.price) || (product.discounted_price && product.discounted_price < product.price)) && (
                          <div className="absolute top-1 sm:top-2 left-1 sm:left-2" style={{ marginTop: product.is_featured ? '28px' : '0' }}>
                            <span className="bg-red-500 text-white text-[10px] sm:text-xs font-medium px-1.5 py-0.5 rounded shadow-sm">
                              {product.discount_percentage ? `-${Math.round(product.discount_percentage)}%` : 'SALE'}
                            </span>
                          </div>
                        )}
                        {calculateTotalStock(product) <= 5 && calculateTotalStock(product) > 0 && (
                          <div className="absolute top-1 sm:top-2 right-1 sm:right-2">
                            <span className="bg-orange-500 text-white text-xs font-medium px-1.5 py-0.5 sm:px-2 sm:py-1 rounded">
                              Low Stock
                            </span>
                          </div>
                        )}
                        {calculateTotalStock(product) === 0 && (
                          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                            <span className="bg-gray-800 text-white text-sm font-medium px-3 py-1 rounded">
                              Out of Stock
                            </span>
                          </div>
                        )}
                        
                        {/* Action Buttons - Show on Hover */}
                        <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 sm:space-x-3 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10">
                          {/* Add to Cart */}
                          <button 
                            className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors shadow-lg"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (product.variants && product.variants.length > 0) {
                                setQuickAddProduct(product);
                                setQuickAddOpen(true);
                              } else {
                                // No variants: add directly
                                addToCart(product);
                                // Button click animation
                                const btn = e.currentTarget as HTMLButtonElement;
                                btn.classList.add('animate-bounce');
                                setTimeout(() => btn.classList.remove('animate-bounce'), 500);
                              }
                            }}
                          >
                            <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5-6m0 0h12.5M17 21a2 2 0 100-4 2 2 0 000 4zM9 21a2 2 0 100-4 2 2 0 000 4z" />
                            </svg>
                          </button>
                          
                          {/* Quick View */}
                          <button 
                            className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-600 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors shadow-lg"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Navigate to product detail page
                              navigate(`/products/${product.slug}`);
                            }}
                          >
                            <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      <div className="p-3 sm:p-4">
                        <div className="mb-1 sm:mb-2">
                          <span className="text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded">
                            {product.product_code}
                          </span>
                        </div>
                        
                        <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2 line-clamp-2">
                          {product.name}
                        </h3>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col">
                            {/* Show promotional price only if there's an active promotion, otherwise show regular price */}
                            <span className="text-sm sm:text-lg font-bold text-gray-900">
                              {(product.has_promotion || (product.promotional_price && product.promotional_price < product.price))
                                ? formatPrice(product.promotional_price || product.price)
                                : (product.discounted_price && product.discounted_price < product.price)
                                ? formatPrice(product.discounted_price || product.price)
                                : formatPrice(product.price)
                              }
                            </span>
                            {/* Show original price as strikethrough only if there's an active promotion */}
                            {((product.has_promotion || (product.promotional_price && product.promotional_price < product.price)) || (product.discounted_price && product.discounted_price < product.price)) && (
                              <span className="text-xs sm:text-sm text-gray-500 line-through">
                                {formatPrice(product.price)}
                              </span>
                            )}
                            {/* Show compare_at_price if no promotion but compare price exists */}
                            {!product.has_promotion && !product.promotional_price && !product.discounted_price && product.compare_at_price && product.compare_at_price > product.price && (
                              <span className="text-xs sm:text-sm text-gray-500 line-through">
                                {formatPrice(product.compare_at_price)}
                              </span>
                            )}
                          </div>
                          
                          <div className="text-right">
                            <div className="text-xs text-gray-500">
                              Stock: {calculateTotalStock(product)}
                            </div>
                          </div>
                        </div>
                        
                        {product.features && product.features.length > 0 && (
                          <div className="mt-1 sm:mt-2">
                            <div className="flex flex-wrap gap-1">
                              {product.features.slice(0, 2).map((feature, index) => (
                                <span key={index} className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded">
                                  {feature}
                                </span>
                              ))}
                              {product.features.length > 2 && (
                                <span className="text-xs text-gray-500">
                                  +{product.features.length - 2} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                  </div>
                ))}
              </div>
            )}

            {/* Quick Add To Cart Modal */}
            <QuickAddToCartModal
              isOpen={quickAddOpen}
              product={quickAddProduct}
              onClose={() => { setQuickAddOpen(false); setQuickAddProduct(null); }}
              onConfirm={(sel) => {
                if (!quickAddProduct) return;
                addToCart(quickAddProduct, sel.quantity, {
                  variantId: sel.variantId,
                  sizeName: sel.sizeName,
                  unitPrice: sel.unitPrice,
                  compareAtPrice: sel.compareAtPrice,
                  inStock: sel.inStock,
                  colorName: sel.colorName,
                  colorCode: sel.colorCode,
                });
                setQuickAddOpen(false);
                setQuickAddProduct(null);
              }}
            />

            {/* Pagination Controls (below grid) */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-between">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 text-sm rounded-md border transition-all ${
                    currentPage === 1
                      ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'text-gray-700 border-gray-300 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]'
                  }`}
                >
                  Previous
                </button>
                <div className="flex items-center gap-2">
                  {getPageNumbers().map(num => (
                    <button
                      key={num}
                      onClick={() => setCurrentPage(num)}
                      className={`w-9 h-9 text-sm rounded-md border transition-all ${
                        currentPage === num
                          ? 'bg-gray-900 text-white border-gray-900 shadow-sm'
                          : 'text-gray-700 border-gray-300 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-2 text-sm rounded-md border transition-all ${
                    currentPage === totalPages
                      ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'text-gray-700 border-gray-300 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]'
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSignature;
